/* eslint-env browser */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, TextField, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import officeInfoService from '../../../services/OfficeInfoService';
import { getPinCodeValidationError } from '../helper/helper';
import useFetchEmployeesPinCode from '../../UserNew/hooks/useFetchEmployeesPinCode';
import prepareQualificationUpload from '../../Qualification/helper/prepareQualificationUpload';
import { publishProfilePictureUrl } from '../../../utils/profilePicture';

const useStyles = makeStyles(() => ({
  textfieldLabel: {
    marginBottom: '5px',
    fontSize: '13px'
  },
  preview: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '1px solid #d8d8d8'
  },
  faceWarning: {
    margin: 0,
    padding: '10px 12px',
    border: '1px solid #ffe082',
    borderRadius: 4,
    backgroundColor: '#fff8e1',
    color: '#795b00',
    fontSize: 13,
    lineHeight: 1.5
  }
}));

const emptyOfficeInfo = {
  officePinCode: '',
  facialRecognitionEnabled: false,
  faceImage: null,
  faceImageUrl: '',
  faceRegistrationPending: false,
  useAsProfilePicture: false,
  profilePictureUrl: ''
};

const OfficeInfo = ({ onProfilePictureChange, onFaceRegistrationPendingChange, refreshKey }) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedValues, setSavedValues] = useState(emptyOfficeInfo);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const { employeesPinCode, employeesPinCodeError, employeesPinCodeLoading } = useFetchEmployeesPinCode();

  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
    formState: { dirtyFields, errors, isDirty }
  } = useForm({ defaultValues: emptyOfficeInfo });

  const facialRecognitionEnabled = watch('facialRecognitionEnabled');
  const faceImage = watch('faceImage');
  const faceImageUrl = watch('faceImageUrl');
  const faceRegistrationPending = watch('faceRegistrationPending');
  const previewUrl = useMemo(() => localPreviewUrl || faceImageUrl || '', [faceImageUrl, localPreviewUrl]);

  useEffect(() => {
    const fetchOfficeInfo = async () => {
      setIsLoading(true);
      try {
        // eslint-disable-next-line no-undef
        const employeeId = localStorage.getItem('userId');
        const response = await officeInfoService.getOfficeInfo({ employeeId });
        const data = response?.data?.data || {};
        const values = {
          officePinCode: data.officePinCode == null ? '' : String(data.officePinCode),
          facialRecognitionEnabled: Boolean(data.facialRecognitionEnabled),
          faceImage: null,
          faceImageUrl: data.faceImageUrl || '',
          faceRegistrationPending: Boolean(data.faceRegistrationPending),
          useAsProfilePicture: Boolean(data.useAsProfilePicture),
          profilePictureUrl: data.profilePictureUrl || ''
        };
        setSavedValues(values);
        reset(values);
        const selectedProfilePictureUrl = values.useAsProfilePicture ? values.profilePictureUrl : '';
        onProfilePictureChange(selectedProfilePictureUrl);
        onFaceRegistrationPendingChange(values.faceRegistrationPending);
        publishProfilePictureUrl(selectedProfilePictureUrl);
      } catch (error) {
        toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Could not show office info');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOfficeInfo();
  }, [onFaceRegistrationPendingChange, onProfilePictureChange, refreshKey, reset]);

  useEffect(
    () => () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    },
    [localPreviewUrl]
  );

  const validatePin = (value) => {
    if (String(value) === String(savedValues.officePinCode)) return true;
    if (employeesPinCodeLoading) return 'PIN availability is still loading. Please try again.';
    if (employeesPinCodeError) return 'PIN availability could not be checked. Please try again.';
    return getPinCodeValidationError(String(value), employeesPinCode, savedValues.officePinCode) || true;
  };

  const handleImageChange = async (event) => {
    if (!facialRecognitionEnabled) return;
    const selectedFile = event.target.files?.[0] || null;
    if (!selectedFile) return;

    const isImage =
      selectedFile.type.startsWith('image/') ||
      /\.(avif|bmp|gif|hei[cf]|jpe?g|png|tiff?|webp)$/i.test(selectedFile.name || '');
    if (!isImage) {
      setError('faceImage', { type: 'validate', message: 'Select a valid image file.' });
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('faceImage', { type: 'validate', message: 'Image must be 10 MB or smaller.' });
      return;
    }

    try {
      const file = await prepareQualificationUpload(selectedFile);
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(URL.createObjectURL(file));
      setValue('faceImage', file, { shouldDirty: true, shouldValidate: true });
    } catch (error) {
      setError('faceImage', {
        type: 'validate',
        message: error?.message || 'The selected image could not be converted.'
      });
    }
  };

  const onSubmit = async (values) => {
    if (values.facialRecognitionEnabled && !values.faceImage && !values.faceImageUrl) {
      setError('faceImage', { type: 'required', message: 'Upload a photo before enabling facial recognition.' });
      return;
    }

    setIsSubmitting(true);
    try {
      // eslint-disable-next-line no-undef
      const employeeId = localStorage.getItem('userId');
      const officeData = { employeeId };
      if (dirtyFields.officePinCode) officeData.officePinCode = values.officePinCode;
      if (dirtyFields.facialRecognitionEnabled || values.faceImage) {
        officeData.facialRecognitionEnabled = values.facialRecognitionEnabled;
      }
      if (values.faceImage) officeData.faceImage = values.faceImage;
      if (dirtyFields.useAsProfilePicture || values.faceImage) {
        officeData.useAsProfilePicture = values.useAsProfilePicture;
      }

      const response = await officeInfoService.updateOfficeInfo(officeData);
      const data = response?.data?.data || {};
      const updatedValues = {
        officePinCode: data.officePinCode == null ? values.officePinCode : String(data.officePinCode),
        facialRecognitionEnabled:
          data.facialRecognitionEnabled == null
            ? values.facialRecognitionEnabled
            : Boolean(data.facialRecognitionEnabled),
        faceImage: null,
        faceImageUrl: data.faceImageUrl || values.faceImageUrl || '',
        faceRegistrationPending: Boolean(data.faceRegistrationPending),
        useAsProfilePicture:
          data.useAsProfilePicture == null ? values.useAsProfilePicture : Boolean(data.useAsProfilePicture),
        profilePictureUrl: data.profilePictureUrl || (data.useAsProfilePicture ? values.profilePictureUrl : '')
      };

      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl('');
      setSavedValues(updatedValues);
      reset(updatedValues);
      const selectedProfilePictureUrl = updatedValues.useAsProfilePicture ? updatedValues.profilePictureUrl : '';
      onProfilePictureChange(selectedProfilePictureUrl);
      onFaceRegistrationPendingChange(updatedValues.faceRegistrationPending);
      publishProfilePictureUrl(selectedProfilePictureUrl);
      toast.success('Office security settings updated successfully');
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Office security settings could not be updated'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <CircularProgress size={24} />;

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
      <Grid container spacing={3} style={{ display: 'flex', alignItems: 'center' }}>
        <Grid item xs={12} sm={3}>
          <p className={classes.textfieldLabel}>Office PIN</p>
          <Controller
            name="officePinCode"
            control={control}
            rules={{ required: 'Office PIN is required.', validate: validatePin }}
            render={({ field }) => (
              <TextField
                {...field}
                required
                variant="outlined"
                size="small"
                type="text"
                inputProps={{ inputMode: 'numeric', maxLength: 4, pattern: '[0-9]*' }}
                fullWidth
                disabled={isSubmitting}
                error={Boolean(errors.officePinCode)}
                helperText={errors.officePinCode?.message}
                onChange={(event) => field.onChange(event.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Controller
            name="facialRecognitionEnabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                      if (!event.target.checked) setValue('useAsProfilePicture', false, { shouldDirty: true });
                    }}
                    disabled={isSubmitting}
                    color="primary"
                  />
                }
                label="Facial recognition"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <input
            {...register('faceImage')}
            id="face-image"
            type="file"
            accept="image/*,.heic,.heif"
            hidden
            disabled={isSubmitting || !facialRecognitionEnabled}
            onChange={handleImageChange}
          />
          <label htmlFor="face-image">
            <Button component="span" variant="outlined" disabled={isSubmitting || !facialRecognitionEnabled}>
              {faceImage || faceImageUrl ? 'Change face photo' : 'Upload face photo'}
            </Button>
          </label>
          {errors.faceImage && <p style={{ color: 'red', fontSize: 12 }}>{errors.faceImage.message}</p>}
        </Grid>

        {previewUrl && (
          <Grid item xs={12} sm={3}>
            <img className={classes.preview} src={previewUrl} alt="Face recognition preview" />
          </Grid>
        )}

        {facialRecognitionEnabled && faceImageUrl && !faceImage && faceRegistrationPending && (
          <Grid item xs={12}>
            <p role="alert" className={classes.faceWarning}>
              Photo uploaded, but Akuvox has not registered the face. If this remains after a few minutes, upload a new
              clear, well-lit, front-facing portrait of one person looking directly at the camera. Keep the entire face
              visible with space around it, and avoid blur, glare, strong backlighting, hats, masks, or side angles.
            </p>
          </Grid>
        )}

        {facialRecognitionEnabled && (faceImage || faceImageUrl) && (
          <Grid item xs={12}>
            <Controller
              name="useAsProfilePicture"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      disabled={isSubmitting}
                      color="primary"
                    />
                  }
                  label="Use this image as my Verd profile picture"
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
          <Button
            type="submit"
            variant="outlined"
            color="primary"
            disabled={isSubmitting || employeesPinCodeLoading || !isDirty}
            style={{ padding: '0.5rem 5rem' }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 10 }} /> <span>Update</span>
              </>
            ) : (
              <span>Update</span>
            )}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

OfficeInfo.propTypes = {
  onProfilePictureChange: PropTypes.func,
  onFaceRegistrationPendingChange: PropTypes.func,
  refreshKey: PropTypes.number
};

OfficeInfo.defaultProps = {
  onProfilePictureChange: () => {},
  onFaceRegistrationPendingChange: () => {},
  refreshKey: 0
};

export default OfficeInfo;
